import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { inventoryAPI, propertyAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Progress } from './ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { useToast } from '../hooks/use-toast';
import {
  Package, AlertTriangle, TrendingDown, ShoppingCart, Plus, RefreshCw,
  ArrowDown, ArrowUp, Box, Droplets, UtensilsCrossed, SprayCan, Home,
  ChevronDown, ChevronUp, Search, Filter, BarChart3, Clock, Minus
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const CATEGORY_ICONS = {
  bedroom: Home,
  bathroom: Droplets,
  kitchen: UtensilsCrossed,
  cleaning: SprayCan,
  general: Box
};

const CATEGORY_COLORS = {
  bedroom: '#6366F1',
  bathroom: '#06B6D4',
  kitchen: '#F59E0B',
  cleaning: '#10B981',
  general: '#8B5CF6'
};

const PIE_COLORS = ['#6366F1', '#06B6D4', '#F59E0B', '#10B981', '#8B5CF6', '#EC4899'];

export default function InventoryManagement() {
  const { token } = useAuth();
  const { toast } = useToast();

  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertyItems, setPropertyItems] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [costAnalytics, setCostAnalytics] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal states
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [restockQty, setRestockQty] = useState(1);
  const [newItem, setNewItem] = useState({
    name: '', category: 'general', unit: 'pieces',
    currentStock: 0, minimumStock: 1, usagePerBooking: 1, unitCost: 0
  });

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const [dashData, propsData] = await Promise.all([
        inventoryAPI.getDashboard(token),
        propertyAPI.getHostProperties(token)
      ]);
      setDashboard(dashData);
      setProperties(propsData.properties || propsData);
    } catch (err) {
      console.error('Failed to load inventory dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadAlerts = useCallback(async () => {
    if (!token) return;
    try {
      const data = await inventoryAPI.getAlerts(token);
      setAlerts(data);
    } catch (err) {
      console.error('Failed to load alerts:', err);
    }
  }, [token]);

  const loadForecast = useCallback(async () => {
    if (!token) return;
    try {
      const data = await inventoryAPI.getForecast(token, 30);
      setForecast(data);
    } catch (err) {
      console.error('Failed to load forecast:', err);
    }
  }, [token]);

  const loadCostAnalytics = useCallback(async () => {
    if (!token) return;
    try {
      const data = await inventoryAPI.getCostAnalytics(token, 6);
      setCostAnalytics(data);
    } catch (err) {
      console.error('Failed to load cost analytics:', err);
    }
  }, [token]);

  const loadPropertyItems = useCallback(async (propertyId) => {
    if (!token || !propertyId) return;
    try {
      const data = await inventoryAPI.getPropertyItems(propertyId, token);
      setPropertyItems(data);
    } catch (err) {
      console.error('Failed to load property items:', err);
    }
  }, [token]);

  useEffect(() => {
    loadDashboard();
    loadAlerts();
  }, [loadDashboard, loadAlerts]);

  useEffect(() => {
    if (activeTab === 'forecast') loadForecast();
    if (activeTab === 'costs') loadCostAnalytics();
  }, [activeTab, loadForecast, loadCostAnalytics]);

  useEffect(() => {
    if (selectedProperty) loadPropertyItems(selectedProperty);
  }, [selectedProperty, loadPropertyItems]);

  // Handlers
  const handleRestock = async () => {
    if (!selectedItem || restockQty <= 0) return;
    try {
      await inventoryAPI.restockItem(selectedItem._id, restockQty, '', token);
      toast({ title: 'Restocked', description: `Added ${restockQty} ${selectedItem.unit} of ${selectedItem.name}` });
      setShowRestockModal(false);
      setRestockQty(1);
      if (selectedProperty) loadPropertyItems(selectedProperty);
      loadDashboard();
      loadAlerts();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to restock item', variant: 'destructive' });
    }
  };

  const handleDismissReport = async (itemId) => {
    try {
      await inventoryAPI.clearReport(itemId, token);
      loadAlerts();
      loadDashboard();
    } catch (err) {
      toast({ title: 'Error', description: 'Could not dismiss the flag', variant: 'destructive' });
    }
  };

  const handleAddItem = async () => {
    if (!selectedProperty || !newItem.name) return;
    try {
      await inventoryAPI.createItem({ ...newItem, propertyId: selectedProperty }, token);
      toast({ title: 'Item Added', description: `${newItem.name} added to inventory` });
      setShowAddItemModal(false);
      setNewItem({ name: '', category: 'general', unit: 'pieces', currentStock: 0, minimumStock: 1, usagePerBooking: 1, unitCost: 0 });
      loadPropertyItems(selectedProperty);
      loadDashboard();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to add item', variant: 'destructive' });
    }
  };

  const handleApplyTemplate = async (templateKey) => {
    if (!selectedProperty) return;
    try {
      const result = await inventoryAPI.applyTemplate(selectedProperty, templateKey, token);
      toast({ title: 'Template Applied', description: result.message });
      setShowTemplateModal(false);
      loadPropertyItems(selectedProperty);
      loadDashboard();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to apply template', variant: 'destructive' });
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await inventoryAPI.deleteItem(itemId, token);
      toast({ title: 'Removed', description: 'Item removed from inventory' });
      if (selectedProperty) loadPropertyItems(selectedProperty);
      loadDashboard();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to remove item', variant: 'destructive' });
    }
  };

  const loadTemplates = async () => {
    try {
      const data = await inventoryAPI.getTemplates(token);
      setTemplates(data.templates);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  // Filtered items
  const filteredItems = propertyItems?.items?.filter(item => {
    if (categoryFilter !== 'all' && item.category !== categoryFilter) return false;
    if (searchQuery && !item.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  }) || [];

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-8 w-8 animate-spin text-[#1F3A68]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#1F3A68]">Inventory Management</h2>
          <p className="text-sm text-gray-500 mt-1">Track supplies, predict usage, and never run out of essentials</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { loadDashboard(); loadAlerts(); }}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <Package className="h-4 w-4" /> Items Tracked
              </div>
              <div className="text-2xl font-bold text-[#1F3A68]">{dashboard.totals.totalItems}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <Home className="h-4 w-4" /> Properties
              </div>
              <div className="text-2xl font-bold text-[#1F3A68]">{dashboard.totals.propertiesTracked}</div>
            </CardContent>
          </Card>
          <Card className={dashboard.totals.lowStockAlerts > 0 ? 'border-amber-300 bg-amber-50/50' : ''}>
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center gap-2 text-sm text-amber-600 mb-1">
                <AlertTriangle className="h-4 w-4" /> Low Stock
              </div>
              <div className="text-2xl font-bold text-amber-600">{dashboard.totals.lowStockAlerts}</div>
            </CardContent>
          </Card>
          <Card className={dashboard.totals.outOfStockAlerts > 0 ? 'border-red-300 bg-red-50/50' : ''}>
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center gap-2 text-sm text-red-600 mb-1">
                <TrendingDown className="h-4 w-4" /> Out of Stock
              </div>
              <div className="text-2xl font-bold text-red-600">{dashboard.totals.outOfStockAlerts}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <ShoppingCart className="h-4 w-4" /> Stock Value
              </div>
              <div className="text-2xl font-bold text-emerald-600">
                £{dashboard.totals.totalStockValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="property" className="relative">
            Property Stock
          </TabsTrigger>
          <TabsTrigger value="alerts" className="relative">
            Alerts
            {alerts?.summary?.totalAlerts > 0 && (
              <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                {alerts.summary.totalAlerts}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="forecast">Forecast</TabsTrigger>
          <TabsTrigger value="costs">Cost Analysis</TabsTrigger>
        </TabsList>

        {/* ── Overview Tab ─────────────────────────────────────────────── */}
        <TabsContent value="overview">
          {dashboard?.properties?.length > 0 ? (
            <div className="grid gap-4">
              {dashboard.properties.map(prop => (
                <Card key={prop.propertyId} className="hover:shadow-md transition-shadow">
                  <CardContent className="py-4 px-5">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-[#1F3A68]">{prop.propertyName}</h3>
                        <div className="flex gap-4 mt-2 text-sm text-gray-500">
                          <span>{prop.totalItems} items</span>
                          <span className="text-emerald-600">£{prop.totalStockValue.toFixed(2)} value</span>
                          <span className="text-gray-400">£{prop.costPerBooking.toFixed(2)}/booking</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {prop.outOfStockItems > 0 && (
                          <Badge variant="destructive">{prop.outOfStockItems} out of stock</Badge>
                        )}
                        {prop.lowStockItems > 0 && (
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200">
                            {prop.lowStockItems} low
                          </Badge>
                        )}
                        {prop.lowStockItems === 0 && prop.outOfStockItems === 0 && (
                          <Badge className="bg-emerald-100 text-emerald-700">All stocked</Badge>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setSelectedProperty(prop.propertyId); setActiveTab('property'); }}
                        >
                          Manage
                        </Button>
                      </div>
                    </div>
                    {/* Stock health bar */}
                    <div className="mt-3">
                      <Progress
                        value={prop.totalItems > 0 ? ((prop.totalItems - prop.lowStockItems) / prop.totalItems) * 100 : 100}
                        className="h-2"
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-500 mb-2">No Inventory Set Up Yet</h3>
                <p className="text-sm text-gray-400 mb-6">
                  Start tracking supplies for your properties. Use our templates to get set up in seconds.
                </p>
                {properties.length > 0 && (
                  <div className="flex justify-center gap-3">
                    <Select onValueChange={(val) => { setSelectedProperty(val); setActiveTab('property'); }}>
                      <SelectTrigger className="w-64">
                        <SelectValue placeholder="Select a property to set up" />
                      </SelectTrigger>
                      <SelectContent>
                        {properties.map(p => (
                          <SelectItem key={p._id} value={p._id}>{p.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Property Stock Tab ───────────────────────────────────────── */}
        <TabsContent value="property">
          {/* Property selector */}
          <div className="flex gap-3 mb-4">
            <Select value={selectedProperty || ''} onValueChange={setSelectedProperty}>
              <SelectTrigger className="w-72">
                <SelectValue placeholder="Select property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map(p => (
                  <SelectItem key={p._id} value={p._id}>{p.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedProperty && (
              <>
                <Button variant="outline" size="sm" onClick={() => { loadTemplates(); setShowTemplateModal(true); }}>
                  <Package className="h-4 w-4 mr-1" /> Apply Template
                </Button>
                <Button size="sm" className="bg-[#1F3A68]" onClick={() => setShowAddItemModal(true)}>
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </>
            )}
          </div>

          {selectedProperty && propertyItems ? (
            <>
              {/* Summary bar */}
              <div className="flex gap-4 mb-4 text-sm">
                <span className="text-gray-500">{propertyItems.summary.totalItems} items</span>
                {propertyItems.summary.lowStock > 0 && (
                  <span className="text-amber-600">{propertyItems.summary.lowStock} low stock</span>
                )}
                {propertyItems.summary.outOfStock > 0 && (
                  <span className="text-red-600">{propertyItems.summary.outOfStock} out of stock</span>
                )}
                <span className="text-emerald-600">£{propertyItems.summary.totalStockValue.toFixed(2)} total value</span>
              </div>

              {/* Filters */}
              <div className="flex gap-3 mb-4">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search items..."
                    className="pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-40">
                    <Filter className="h-4 w-4 mr-1" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="bedroom">Bedroom</SelectItem>
                    <SelectItem value="bathroom">Bathroom</SelectItem>
                    <SelectItem value="kitchen">Kitchen</SelectItem>
                    <SelectItem value="cleaning">Cleaning</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Items grid */}
              <div className="grid gap-2">
                {filteredItems.length > 0 ? filteredItems.map(item => {
                  const Icon = CATEGORY_ICONS[item.category] || Box;
                  const stockPct = item.minimumStock > 0
                    ? Math.min(100, (item.currentStock / (item.minimumStock * 3)) * 100)
                    : 100;
                  const stockColor = item.isOutOfStock ? 'bg-red-500' : (item.isLowStock ? 'bg-amber-500' : 'bg-emerald-500');

                  return (
                    <Card key={item._id} className={`${item.isOutOfStock ? 'border-red-200 bg-red-50/30' : item.isLowStock ? 'border-amber-200 bg-amber-50/30' : ''}`}>
                      <CardContent className="py-3 px-4">
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-lg" style={{ backgroundColor: `${CATEGORY_COLORS[item.category]}15` }}>
                            <Icon className="h-5 w-5" style={{ color: CATEGORY_COLORS[item.category] }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{item.name}</span>
                              <Badge variant="outline" className="text-xs capitalize">{item.category}</Badge>
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                              <div className="w-32">
                                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${stockColor}`} style={{ width: `${stockPct}%` }} />
                                </div>
                              </div>
                              <span className={`text-xs font-medium ${item.isOutOfStock ? 'text-red-600' : item.isLowStock ? 'text-amber-600' : 'text-gray-500'}`}>
                                {item.currentStock} {item.unit}
                              </span>
                              <span className="text-xs text-gray-400">
                                (min: {item.minimumStock})
                              </span>
                              <span className="text-xs text-gray-400">
                                {item.usagePerBooking}/{' '}booking
                              </span>
                              <span className="text-xs text-gray-400">
                                £{item.unitCost.toFixed(2)}/unit
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 px-2"
                                    onClick={() => { setSelectedItem(item); setRestockQty(Math.max(1, item.minimumStock - item.currentStock + Math.ceil(item.usagePerBooking * 3))); setShowRestockModal(true); }}
                                  >
                                    <ArrowUp className="h-3.5 w-3.5 text-emerald-600" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Restock</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-2 text-red-400 hover:text-red-600"
                                    onClick={() => handleDeleteItem(item._id)}
                                  >
                                    <Minus className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Remove</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                }) : (
                  <Card>
                    <CardContent className="py-8 text-center text-gray-400">
                      {propertyItems?.items?.length === 0
                        ? 'No inventory items yet. Apply a template or add items manually.'
                        : 'No items match your filters.'}
                    </CardContent>
                  </Card>
                )}
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-gray-400">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                Select a property to manage its inventory
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Alerts Tab ───────────────────────────────────────────────── */}
        <TabsContent value="alerts">
          {alerts?.alerts?.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span>{alerts.summary.totalAlerts} items need attention</span>
                {alerts.summary.outOfStock > 0 && (
                  <Badge variant="destructive">{alerts.summary.outOfStock} out of stock</Badge>
                )}
                <span>{alerts.summary.propertiesAffected} properties affected</span>
                <span className="text-amber-600">Est. restock cost: £{alerts.summary.estimatedRestockCost.toFixed(2)}</span>
              </div>

              {alerts.alerts.map(alert => (
                <Card key={alert._id} className={alert.isOutOfStock ? 'border-red-300 bg-red-50/50' : 'border-amber-300 bg-amber-50/50'}>
                  <CardContent className="py-3 px-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          {alert.isOutOfStock ? (
                            <TrendingDown className="h-4 w-4 text-red-500" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                          )}
                          <span className="font-medium">{alert.name}</span>
                          <Badge variant="outline" className="text-xs">{alert.propertyName}</Badge>
                          {alert.reportedLow && (
                            <Badge className="text-xs bg-indigo-100 text-indigo-700 border-indigo-200">
                              Flagged by {alert.reportedLowBy || 'your cleaner'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1 ml-6">
                          {alert.currentStock} {alert.unit} remaining (minimum: {alert.minimumStock})
                          {alert.stockDeficit > 0 && ` — need ${alert.stockDeficit} more`}
                          {alert.restockCost > 0 && ` (£${alert.restockCost.toFixed(2)})`}
                        </p>
                        {alert.reportedLow && alert.reportedLowNote && (
                          <p className="text-sm text-indigo-700 mt-1 ml-6 italic">“{alert.reportedLowNote}”</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {alert.reportedLow && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDismissReport(alert._id)}
                          >
                            Dismiss
                          </Button>
                        )}
                        <Button
                          size="sm"
                          className={alert.isOutOfStock ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
                          onClick={() => {
                            setSelectedItem(alert);
                            setRestockQty(Math.max(1, alert.stockDeficit + Math.ceil(alert.minimumStock * 0.5)));
                            setShowRestockModal(true);
                          }}
                        >
                          <ShoppingCart className="h-3.5 w-3.5 mr-1" /> Restock
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="text-emerald-500 mb-2">
                  <Package className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-semibold text-emerald-600">All Stocked Up!</h3>
                <p className="text-sm text-gray-400 mt-1">No items are below their minimum threshold.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Forecast Tab ─────────────────────────────────────────────── */}
        <TabsContent value="forecast">
          {forecast ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Upcoming Bookings</div>
                    <div className="text-2xl font-bold text-[#1F3A68]">{forecast.upcomingBookings}</div>
                    <div className="text-xs text-gray-400">Next {forecast.forecastDays} days</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Items Need Restock</div>
                    <div className="text-2xl font-bold text-amber-600">{forecast.summary.itemsNeedingRestock}</div>
                  </CardContent>
                </Card>
                <Card className={forecast.summary.criticalItems > 0 ? 'border-red-300' : ''}>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-red-500 mb-1">Will Run Out</div>
                    <div className="text-2xl font-bold text-red-600">{forecast.summary.criticalItems}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Est. Restock Cost</div>
                    <div className="text-2xl font-bold text-emerald-600">
                      £{forecast.summary.estimatedRestockCost.toFixed(2)}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {forecast.items.length > 0 ? (
                <div className="grid gap-2">
                  {forecast.items.map(item => (
                    <Card key={item._id} className={item.urgency === 'critical' ? 'border-red-200' : 'border-amber-200'}>
                      <CardContent className="py-3 px-5">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Badge variant={item.urgency === 'critical' ? 'destructive' : 'default'} className={item.urgency !== 'critical' ? 'bg-amber-100 text-amber-700' : ''}>
                                {item.urgency}
                              </Badge>
                              <span className="font-medium">{item.name}</span>
                              <span className="text-xs text-gray-400">({item.propertyName})</span>
                            </div>
                            <div className="flex gap-4 mt-1 text-sm text-gray-500 ml-0">
                              <span>Current: {item.currentStock} {item.unit}</span>
                              <span className="text-amber-600">
                                <ArrowDown className="h-3 w-3 inline" /> {item.projectedUsage} expected usage
                              </span>
                              <span className={item.willRunOut ? 'text-red-600 font-medium' : 'text-amber-600'}>
                                Projected: {item.projectedStock} {item.unit}
                              </span>
                              <span className="text-[#1F3A68]">
                                Restock: {item.restockRecommended} {item.unit} (£{item.restockCost.toFixed(2)})
                              </span>
                            </div>
                          </div>
                          <div className="text-xs text-gray-400">
                            {item.upcomingBookings} bookings
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="py-8 text-center text-emerald-600">
                    Your inventory levels look good for the next {forecast.forecastDays} days.
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          )}
        </TabsContent>

        {/* ── Cost Analysis Tab ────────────────────────────────────────── */}
        <TabsContent value="costs">
          {costAnalytics ? (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Total Inventory Spend</div>
                    <div className="text-2xl font-bold text-[#1F3A68]">
                      £{costAnalytics.totalSpend.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-400">Last 6 months</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Cost Per Booking</div>
                    <div className="text-2xl font-bold text-amber-600">
                      £{costAnalytics.costPerBooking.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-400">{costAnalytics.bookingCount} bookings</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="text-sm text-gray-500 mb-1">Avg Monthly Spend</div>
                    <div className="text-2xl font-bold text-[#1F3A68]">
                      £{costAnalytics.monthlySpend.length > 0
                        ? (costAnalytics.totalSpend / costAnalytics.monthlySpend.length).toFixed(2)
                        : '0.00'}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Monthly spend chart */}
                {costAnalytics.monthlySpend.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Monthly Inventory Spend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={costAnalytics.monthlySpend}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `£${v}`} />
                          <RechartsTooltip formatter={(v) => [`£${v.toFixed(2)}`, 'Spend']} />
                          <Bar dataKey="amount" fill="#1F3A68" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Category breakdown pie */}
                {costAnalytics.categoryBreakdown.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Spend by Category</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4">
                        <ResponsiveContainer width="50%" height={180}>
                          <PieChart>
                            <Pie
                              data={costAnalytics.categoryBreakdown}
                              dataKey="amount"
                              nameKey="category"
                              cx="50%" cy="50%"
                              outerRadius={70}
                              innerRadius={40}
                            >
                              {costAnalytics.categoryBreakdown.map((_, idx) => (
                                <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <RechartsTooltip formatter={(v) => `£${v.toFixed(2)}`} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="flex-1 space-y-2">
                          {costAnalytics.categoryBreakdown.map((cat, idx) => (
                            <div key={cat.category} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                                <span className="capitalize">{cat.category}</span>
                              </div>
                              <span className="font-medium">£{cat.amount.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ── Restock Modal ──────────────────────────────────────────────── */}
      <Dialog open={showRestockModal} onOpenChange={setShowRestockModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restock: {selectedItem?.name}</DialogTitle>
            <DialogDescription>
              Current stock: {selectedItem?.currentStock} {selectedItem?.unit}
              {selectedItem?.minimumStock && ` (minimum: ${selectedItem.minimumStock})`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Quantity to Add</Label>
              <Input
                type="number"
                min="1"
                value={restockQty}
                onChange={(e) => setRestockQty(parseInt(e.target.value) || 0)}
              />
              {selectedItem?.unitCost > 0 && (
                <p className="text-sm text-gray-400 mt-1">
                  Estimated cost: £{(restockQty * selectedItem.unitCost).toFixed(2)}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRestockModal(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleRestock}>
              <ArrowUp className="h-4 w-4 mr-1" /> Restock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Add Item Modal ─────────────────────────────────────────────── */}
      <Dialog open={showAddItemModal} onOpenChange={setShowAddItemModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Inventory Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  placeholder="e.g. Bath Towels"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bedroom">Bedroom</SelectItem>
                    <SelectItem value="bathroom">Bathroom</SelectItem>
                    <SelectItem value="kitchen">Kitchen</SelectItem>
                    <SelectItem value="cleaning">Cleaning</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unit</Label>
                <Input
                  value={newItem.unit}
                  onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                  placeholder="pieces, bottles, rolls"
                />
              </div>
              <div>
                <Label>Unit Cost (£)</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={newItem.unitCost}
                  onChange={(e) => setNewItem({ ...newItem, unitCost: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Current Stock</Label>
                <Input
                  type="number" min="0"
                  value={newItem.currentStock}
                  onChange={(e) => setNewItem({ ...newItem, currentStock: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Minimum Stock</Label>
                <Input
                  type="number" min="0"
                  value={newItem.minimumStock}
                  onChange={(e) => setNewItem({ ...newItem, minimumStock: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Usage / Booking</Label>
                <Input
                  type="number" min="0" step="0.1"
                  value={newItem.usagePerBooking}
                  onChange={(e) => setNewItem({ ...newItem, usagePerBooking: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItemModal(false)}>Cancel</Button>
            <Button className="bg-[#1F3A68]" onClick={handleAddItem}>
              <Plus className="h-4 w-4 mr-1" /> Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Template Modal ─────────────────────────────────────────────── */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Apply Inventory Template</DialogTitle>
            <DialogDescription>
              Choose a template based on your property size. This will add standard consumable items with recommended stock levels.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {templates ? templates.map(tmpl => (
              <Card key={tmpl.key} className="cursor-pointer hover:border-[#1F3A68] transition-colors"
                onClick={() => handleApplyTemplate(tmpl.key)}>
                <CardContent className="py-3 px-4 flex items-center justify-between">
                  <div>
                    <div className="font-medium">{tmpl.label}</div>
                    <div className="text-sm text-gray-400">{tmpl.itemCount} items across 5 categories</div>
                  </div>
                  <Button size="sm" variant="outline">Apply</Button>
                </CardContent>
              </Card>
            )) : (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
